import { useCallback, useEffect, useMemo, useState } from 'react';
import { useUser } from '../../../hooks/queries/useAuth';
import { useProjects } from '../../../hooks/queries/useProjects';
import {
  useDeleteDashboardTask,
  useFilteredDashboardContent,
  useUpdateDashboardTask,
} from '../../../hooks/queries/useDashboard';
import { TaskItem } from '../types/dashboardTypes';
import { logger } from '../../../utils/logger';
import { useDashboardUIStore } from '../../../store/dashboardUIStore';

/**
 * Enhanced hook to access dashboard data using React Query
 * Provides a simple interface with data loading, filtering, and task management
 *
 * NOTE: Offline mode support was planned but not implemented.
 * When adding offline support, consider:
 * - useAppConnectionState for isOnline status
 * - useQueryErrorHandler for standardized error handling
 * - getConnectionState for non-hook contexts
 */
export const useDashboardData = () => {
  // Current user from React Query
  const { data: user } = useUser();
  const currentUserId = user?.id;

  // Get project list from React Query hook for shared status check
  const { data: projects = [] } = useProjects();

  // Get filter state from Zustand store
  const {
    activeFilters,
    setUserFilter,
    setProjectFilter,
    setTagFilter,
    resetFilters: resetFiltersStore,
    initializeFilters,
    saveFiltersToStorage,
    loadFiltersFromStorage,
  } = useDashboardUIStore();

  // Track if filters have been initialized
  const [filtersInitialized, setFiltersInitialized] = useState(false);

  // Track editing task
  const [editingTask, setEditingTask] = useState<TaskItem | null>(null);

  // Initialize filters with current user when data is available
  useEffect(() => {
    if (currentUserId && !filtersInitialized) {
      // Initialize filters (will load from storage or set current user as default)
      initializeFilters(currentUserId);
      setFiltersInitialized(true);
    }
  }, [currentUserId, filtersInitialized, initializeFilters]);

  // Helper function to check if a project is shared
  const projectIsSharedFunc = useCallback(
    (projectId: string) => {
      const project = projects.find(p => p.id === projectId);
      return project?.isShared === true;
    },
    [projects]
  );

  // Fetch and filter dashboard content
  const {
    data: dashboardData,
    filteredData: categorizedTasks,
    isLoading,
    error,
    refetch,
  } = useFilteredDashboardContent(activeFilters, currentUserId, projectIsSharedFunc);

  // Task update mutation
  const updateTaskMutation = useUpdateDashboardTask();

  // Task delete mutation
  const deleteTaskMutation = useDeleteDashboardTask();

  // Handler to update a task
  const handleUpdateTask = useCallback(
    async (taskId: string, updates: Partial<TaskItem>) => {
      try {
        logger.component('Updating dashboard task', { taskId, updates });

        // Execute the mutation
        await updateTaskMutation.mutateAsync({ taskId, updates });

        return true;
      } catch (error) {
        // Error handling is already done in the mutation
        // Just log and return false to indicate failure
        logger.error('Error in handleUpdateTask:', error);
        return false;
      }
    },
    [updateTaskMutation]
  );

  // Handler to delete a task
  const handleDeleteTask = useCallback(
    async (taskId: string, topicId?: string) => {
      try {
        logger.component('Deleting dashboard task', { taskId, topicId });

        // Execute the mutation with topicId if provided
        await deleteTaskMutation.mutateAsync({ taskId, topicId });
        return true;
      } catch (error) {
        // Error handling is already done in the mutation
        // Just log and return false to indicate failure
        logger.error('Error in handleDeleteTask:', error);
        return false;
      }
    },
    [deleteTaskMutation]
  );

  // Wrapper for reset filters to pass current user ID
  const resetFilters = useCallback(() => {
    resetFiltersStore(currentUserId);
  }, [currentUserId, resetFiltersStore]);

  // Auto-save filters when they change
  useEffect(() => {
    saveFiltersToStorage();
  }, [activeFilters, saveFiltersToStorage]);

  // Extract all users
  const users = useMemo(() => dashboardData?.users || [], [dashboardData]);

  // Extract all projects
  const projectsForFiltering = useMemo(() => dashboardData?.projects || [], [dashboardData]);

  // Extract categorized tasks with defensive handling
  // categorizedTasks should always be an object now, but adding extra safety
  const {
    highlightedTasks = [],
    overdueTasks = [],
    todayTasks = [],
    thisWeekTasks = [],
    availableTags = [],
  } = categorizedTasks || {
    highlightedTasks: [],
    overdueTasks: [],
    todayTasks: [],
    thisWeekTasks: [],
    availableTags: [],
  };

  // Return everything needed by the dashboard component
  return {
    // Data loading state
    isLoading,
    error,
    hasFetched: !!dashboardData,
    hasFilterableData: !!(dashboardData?.tasks.length && dashboardData?.users.length),

    // Categorized tasks
    highlightedTasks,
    overdueTasks,
    todayTasks,
    thisWeekTasks,

    // Filter state and actions
    activeFilters,
    userFilters: activeFilters.userIds,
    projectFilters: activeFilters.projectIds,
    selectedTags: activeFilters.tags,
    resetFilters,
    loadDashboardFilters: loadFiltersFromStorage,

    // Functions needed by Dashboard component
    setUserFilter,
    setProjectFilter,
    setTagFilter,
    saveDashboardFilters: saveFiltersToStorage,

    // Initialization state
    initUserFilter: filtersInitialized ? currentUserId : null,

    // Raw data needed by components
    currentUserId,
    rawTasks: dashboardData?.tasks || [],
    projects: projectsForFiltering,
    users,
    availableTags,

    // Task management functions
    editingTask,
    setEditingTask,
    handleUpdateTask,
    handleDeleteTask,

    // Data refreshing
    silentlyRefreshDashboard: refetch,
  };
};
